import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, VendorTag } from "../components/layout/AppLayout";
import { Input } from "../components/ui/input";
import { VENDOR_CATEGORIES, engineerMatchesVendorFilter } from "../lib/vendorCategories";
import { CaretDown, CaretRight } from "@phosphor-icons/react";

export default function Talent() {
  const [engineers, setEngineers] = useState([]);
  const [filter, setFilter] = useState("");
  // Set<string> — category keys e.g. "Fortinet". Default empty = show all.
  const [selectedCats, setSelectedCats] = useState(() => new Set());
  // Set<string> — granular tech names e.g. "FortiSASE and ZTNA". Expanded view.
  const [selectedTechs, setSelectedTechs] = useState(() => new Set());
  // Which category tiles are expanded to show their granular tech chips.
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => { (async () => setEngineers((await api.get("/talent-pool")).data))(); }, []);

  const toggleSet = (setter, value) => setter((prev) => {
    const next = new Set(prev);
    next.has(value) ? next.delete(value) : next.add(value);
    return next;
  });

  const filtered = engineers.filter((e) => {
    const q = filter.toLowerCase();
    const matchQ = !q || e.name.toLowerCase().includes(q) || (e.role_title || "").toLowerCase().includes(q) || (e.certifications || []).some(c => c.code?.toLowerCase().includes(q));
    const matchV = engineerMatchesVendorFilter(e.vendors || [], selectedCats, selectedTechs);
    return matchQ && matchV;
  });

  const clearFilters = () => { setSelectedCats(new Set()); setSelectedTechs(new Set()); setExpanded(new Set()); };
  const activeCount = selectedCats.size + selectedTechs.size;

  return (
    <div>
      <PageHeader
        overline="Human Layer · Read-only view"
        title="Talent Pool"
        subtitle="Every certified engineer. Data flows in from the Personnel tab — HR maintains it there."
      />

      <div className="mb-4 bg-white border border-slate-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Input data-testid="talent-search" placeholder="Search name, cert, role…" value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-sm bg-white border-slate-300" />
          {activeCount > 0 && (
            <button data-testid="talent-clear-filters" onClick={clearFilters} className="text-xs font-mono uppercase tracking-wider text-slate-500 hover:text-red-600" style={{transition:"color 0.15s ease"}}>Clear filters ({activeCount})</button>
          )}
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">Filter by Vendor</div>
          <div className="flex flex-wrap gap-2">
            {VENDOR_CATEGORIES.map((c) => {
              const isSel = selectedCats.has(c.key);
              const isExp = expanded.has(c.key);
              const hasChildren = c.techs.length > 1;
              return (
                <div key={c.key} className="inline-flex items-stretch rounded border border-slate-300 bg-white overflow-hidden">
                  <button
                    type="button"
                    data-testid={`vendor-cat-${c.key}`}
                    onClick={() => toggleSet(setSelectedCats, c.key)}
                    className={`px-3 py-1.5 text-xs font-mono uppercase ${isSel ? "bg-[#FFC107] text-[#0F1E38]" : "text-slate-600 hover:bg-slate-50"}`}
                    style={{transition:"background-color 0.15s ease"}}
                  >
                    {c.label}
                    {isSel && <span className="ml-1 text-[9px]">✓</span>}
                  </button>
                  {hasChildren && (
                    <button
                      type="button"
                      data-testid={`vendor-expand-${c.key}`}
                      onClick={() => toggleSet(setExpanded, c.key)}
                      className={`px-2 border-l border-slate-300 ${isExp ? "bg-slate-100" : "hover:bg-slate-50"}`}
                      style={{transition:"background-color 0.15s ease"}}
                      title={isExp ? "Collapse" : "Expand to pick specific products"}
                    >
                      {isExp ? <CaretDown size={10} /> : <CaretRight size={10} />}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {VENDOR_CATEGORIES.some((c) => expanded.has(c.key) && c.techs.length > 1) && (
            <div className="mt-3 pl-3 border-l-2 border-amber-200 space-y-2">
              {VENDOR_CATEGORIES.filter((c) => expanded.has(c.key) && c.techs.length > 1).map((c) => (
                <div key={`exp-${c.key}`}>
                  <div className="text-[10px] font-mono uppercase text-slate-400 mb-1">{c.label} products</div>
                  <div className="flex flex-wrap gap-1.5">
                    {c.techs.map((t) => (
                      <button
                        key={t}
                        type="button"
                        data-testid={`vendor-tech-${t.replace(/\s+/g, "-").toLowerCase()}`}
                        onClick={() => toggleSet(setSelectedTechs, t)}
                        className={`px-2 py-1 rounded text-[11px] border ${selectedTechs.has(t) ? "bg-amber-100 border-amber-400 text-[#0F1E38] font-semibold" : "bg-white border-slate-300 text-slate-500 hover:border-slate-400"}`}
                        style={{transition:"all 0.15s ease"}}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="border border-slate-200 bg-white rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left overline border-b border-slate-200">
              <th className="px-4 py-3">Engineer</th><th>Role</th><th>Vendors / Certs</th><th className="text-right">Day Rate</th><th>Utilization</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.id} data-testid={`eng-row-${e.id}`} className="border-b border-slate-100 hover:bg-slate-50" style={{transition:"background-color 0.15s ease"}}>
                <td className="px-4 py-3">
                  <div className="font-medium text-[#0F1E38]">{e.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{e.email}</div>
                </td>
                <td className="text-slate-700">{e.role_title}</td>
                <td>
                  <div className="flex flex-wrap gap-1">{e.vendors?.map(v => <VendorTag key={v} vendor={v}/>)}</div>
                  <div className="text-[10px] font-mono text-emerald-400 mt-1">{e.certifications?.map(c => c.code).join(" · ")}</div>
                </td>
                <td className="text-right font-mono text-[#0F1E38]">${e.day_rate}/d</td>
                <td>
                  <div className="w-24 h-1.5 bg-slate-200 rounded overflow-hidden"><div className={`h-full ${e.utilization > 85 ? "bg-red-500" : e.utilization > 60 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${e.utilization}%`, transition:"width 0.4s ease" }} /></div>
                  <div className="text-[10px] font-mono text-slate-500 mt-0.5">{e.utilization}%</div>
                </td>
                <td><span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${e.status === "available" ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-amber-400 border-amber-500/40 bg-amber-500/10"}`}>{e.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="grid-bg py-16 text-center text-sm text-slate-500">No engineers matched.</div>}
      </div>
    </div>
  );
}
